import React, { useEffect, useState } from "react";
import {
  Container,
  Form,
  Button,
  Card,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import { HelpModal } from "../../common/compoents/HelpModal";
import { rewriteOptimizeContent } from "../../../services/getAiResponse";
import "../style.css";
import { ToolsHeader } from "../../components/ToolsHeader";
import { isInIframe } from "../../../utils/is-in-iframe";
import { validateArticleContent } from "../../../utils/validators";

export const RewriteOptimizeContent: React.FC = () => {
  const [content, setContent] = useState<string>("");
  const [language, setLanguage] = useState<string>("English");
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);
  const [rewrittenContent, setRewrittenContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLanguage(e.target.value);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
  };

  const handleRewrite = async () => {
    const validationError = validateArticleContent(content);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await rewriteOptimizeContent(content, language);
      const data = JSON.parse(response);
      setRewrittenContent(data.rewrittenText);
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } catch (err) {
      console.error("Error rewriting content:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = rewrittenContent;
    document.body.appendChild(tempDiv);

    const range = document.createRange();
    range.selectNodeContents(tempDiv);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
      document.execCommand("copy");
      selection.removeAllRanges();
    }

    document.body.removeChild(tempDiv);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [rewrittenContent]);

  return (
    <Container
      className={`py-3 py-md-5 ${
        !isInIframe ? "defaultText" : "interDisplay"
      }`}
      fluid="sm"
    >
      <Row className="g-3">
        <Col xs={12}>
          <HelpModal
            helpSteps={[
              "Select the language of the content you want to rewrite.",
              "Paste the content into the designated text area.",
              "Click 'Start Rewriting' to generate the rewritten content.",
            ]}
            show={showVideoModal}
            close={closeVideoModal}
            videoUrl="https://www.youtube.com/embed/hNce9hFpBOY"
          />

          {!isInIframe && (
            <ToolsHeader
              title="Rewrite Content with AI"
              description="Smart tool to rewrite articles or content. Paste text to rephrase it in different languages."
            />
          )}

          <Card className="p-3 p-md-4">
            <Form.Group controlId="languageSelect" className="mb-3">
              <Form.Label>Language</Form.Label>
              <Form.Select value={language} onChange={handleLanguageChange}>
                <option value="English">English</option>
                <option value="Hindi">Hindi</option>
                <option value="French">French</option>
                <option value="Spanish">Spanish</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="contentInput" className="mb-3">
              <Form.Label>What would you like to rewrite?</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Paste the content or the URL of the article that you want to rewrite"
                value={content}
                onChange={handleContentChange}
                style={{ resize: "vertical", minHeight: "100px" }}
                isInvalid={!!error}
              />
              <Form.Control.Feedback type="invalid">
                {error}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="text-center">
              {isLoading ? (
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              ) : (
                <Button
                  style={{ backgroundColor: "#8cc540" }}
                  onClick={handleRewrite}
                  size="lg"
                  className="px-4 classRewriteButton"
                >
                  Start Rewriting
                </Button>
              )}
            </div>

            {rewrittenContent && (
              <Card className="mt-3 p-3">
                <h4 className="h5">Rewritten Content</h4>
                <div
                  className="rewritten-content"
                  dangerouslySetInnerHTML={{
                    __html: rewrittenContent,
                  }}
                />
                <div className="d-flex align-items-center mt-3">
                  <Button
                    variant="outline-secondary"
                    onClick={handleCopy}
                    className="px-3 classRewriteButton"
                  >
                    Copy
                  </Button>
                  {copied && (
                    <span className="ms-2 text-success">Copied!</span>
                  )}
                </div>
              </Card>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};
